import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Zap, Trophy, Rocket, LineChart, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import OpenAIBadge from "@/components/OpenAIBadge";
import { CALENDLY } from "@/data";
import { trackContentsView } from "@/lib/tracking";

const BENEFITS = [
  { icon: Rocket, title: "First-access to ChatGPT ad inventory", desc: "Priority access to new placements, formats and betas across OpenAI's advertising surfaces — before they're generally available." },
  { icon: LineChart, title: "Advanced AI-native optimisation", desc: "Bidding, audience modelling and creative iteration engineered specifically for OpenAI's ranking and attribution stack." },
  { icon: Sparkles, title: "Purpose-built creative for LLM surfaces", desc: "Prompt-optimised copy, imagery and structured data crafted for how AI answers surface intent — not for legacy feeds." },
  { icon: Zap, title: "Direct OpenAI partner support", desc: "Escalation paths, roadmap briefings and quarterly reviews you don't get with a generic media buy." },
  { icon: ShieldCheck, title: "Verified partner status", desc: "Officially recognised by OpenAI — one of the few in India — so your brand plays with the platform's trust, not against it." },
  { icon: Trophy, title: "Category exclusivity where it matters", desc: "We deliberately cap onboarding to protect the strategies we build for you inside your category." },
];

export default function OpenAIPartner() {
  useEffect(() => {
    trackContentsView("openai-partner");
  }, []);
  return (
    <main className="relative bg-[#050505] text-white min-h-screen" data-testid="openai-partner-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(rgba(16,163,127,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(16,163,127,0.15) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse at 50% 20%, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at 50% 20%, black 30%, transparent 75%)",
          }}
        />
        <div className="absolute -top-24 left-1/4 h-96 w-96 rounded-full bg-[#10a37f]/25 blur-3xl" />
        <div className="absolute top-1/3 right-0 h-72 w-72 rounded-full bg-[#10a37f]/15 blur-3xl" />

        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_auto] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10a37f]/40 bg-[#10a37f]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.28em] text-[#34d399]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#34d399] animate-pulse" />
              Exclusive · India
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 font-display text-5xl md:text-7xl font-extrabold tracking-tighter leading-[0.92]"
            >
              <span className="bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent">
                Advertise where
              </span>
              <br />
              <span className="text-[#34d399]">people ask</span>
              <span className="bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent">, not scroll.</span>
            </motion.h1>
            <p className="mt-6 font-body text-white/70 leading-relaxed max-w-[52ch]">
              DNA Digital is a <strong className="text-white">Verified OpenAI Ads Partner</strong> — one of the few
              agencies in India accredited to plan, buy and optimise ads across ChatGPT and OpenAI's ad surfaces.
              This isn't another media channel; it's a new attention economy, and we're one of the few teams already
              running inside it.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/#audit"
                data-testid="oai-hero-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-[#10a37f] pl-7 pr-2.5 py-3.5 text-black font-body font-bold shadow-[0_0_40px_-8px_rgba(16,163,127,0.6)] hover:shadow-[0_0_60px_-8px_rgba(52,211,153,0.9)] transition-shadow"
              >
                Explore Exclusive AI Ads
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black transition-transform duration-500 group-hover:rotate-45">
                  <ArrowUpRight className="h-5 w-5 text-[#34d399]" />
                </span>
              </Link>
              <div className="inline-flex items-center gap-2 text-xs font-body font-semibold text-[#34d399]">
                <span className="h-2 w-2 rounded-full bg-[#34d399] animate-pulse shadow-[0_0_10px_#34d399]" />
                Limited Capacity: Only 5 new AI Ads clients this month.
              </div>
            </div>
          </div>

          <div className="justify-self-center lg:justify-self-end">
            <OpenAIBadge size={220} />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-24 border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <span className="font-body text-xs font-bold uppercase tracking-[0.28em] text-[#34d399]">
            What partnership unlocks
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-[18ch]">
            <span className="bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent">
              Advantages you can't buy off the shelf.
            </span>
          </h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-7 hover:border-[#10a37f]/50 transition-colors"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#10a37f]/12 border border-[#10a37f]/30 text-[#34d399]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{b.title}</h3>
                  <p className="mt-2 font-body text-sm text-white/60 leading-relaxed">{b.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Exclusivity stats band */}
      <section className="relative py-20 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,163,127,0.18),transparent_60%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 grid md:grid-cols-3 gap-8 text-center">
          {[
            { v: "1 of few", l: "OpenAI Ads Partners in India" },
            { v: "5", l: "New AI Ads clients accepted this month" },
            { v: "24/7", l: "Direct partner support & escalation" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-5xl md:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-white to-[#34d399] bg-clip-text text-transparent">
                {s.v}
              </div>
              <div className="mt-2 font-body text-sm text-white/60">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <span className="font-body text-xs font-bold uppercase tracking-[0.28em] text-[#34d399]">
            Talk to the AI Ads team
          </span>
          <h2 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95]">
            <span className="bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent">
              5 seats. Category exclusive. This month only.
            </span>
          </h2>
          <p className="mt-6 font-body text-white/60 max-w-[52ch] mx-auto">
            Every AI Ads engagement gets a founder-led strategy, a bespoke playbook, and category-level exclusivity while you're on the roster.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#audit"
              data-testid="oai-final-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#10a37f] pl-7 pr-2.5 py-3.5 text-black font-body font-bold shadow-[0_0_40px_-8px_rgba(16,163,127,0.6)] hover:shadow-[0_0_60px_-8px_rgba(52,211,153,0.9)] transition-shadow"
            >
              Explore Exclusive AI Ads
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black transition-transform duration-500 group-hover:rotate-45">
                <ArrowUpRight className="h-5 w-5 text-[#34d399]" />
              </span>
            </Link>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-body font-semibold text-white hover:bg-white hover:text-black transition-colors duration-300"
            >
              Book a 1-on-1 Call
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
